import { supabase } from "./supabase";
import { getProductById as getStaticProduct, PRODUCTS } from "../data/products";

const STORAGE_BUCKET = "products";

/**
 * Resolve image URL - handles full URLs, base64, and Supabase Storage paths
 */
function resolveImageUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== "string") return getPlaceholderImage();
  const trimmed = imageUrl.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  if (trimmed.startsWith("data:")) return trimmed;
  if (trimmed.startsWith("/")) return trimmed; // CRA static assets e.g. /static/media/xxx.png
  // Supabase Storage path - get public URL
  try {
    if (supabase?.storage) {
      const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(trimmed);
      return data?.publicUrl || getPlaceholderImage();
    }
  } catch {
    // ignore
  }
  return getPlaceholderImage();
}

/** Inline placeholder (no external fetch) */
function getPlaceholderImage() {
  return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='450' viewBox='0 0 400 450'%3E%3Crect fill='%23f3f3f3' width='400' height='450'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='18' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(str) {
  return str && UUID_REGEX.test(str);
}

/**
 * Generate URL-friendly slug from product name
 */
export function slugify(name) {
  if (!name || typeof name !== "string") return "";
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "product";
}

/**
 * Parse pathname to get product identifier (slug or UUID)
 * - /product/chintu -> "chintu"
 * - /product/abc-123-uuid -> "abc-123-uuid" (Supabase UUID)
 * - /gold-bracelet-1 -> "gold-bracelet-1" (static slug)
 */
export function parseProductIdFromPath(pathname) {
  const path = pathname.startsWith("/") ? pathname.slice(1) : pathname;
  if (path.startsWith("product/")) {
    return path.replace("product/", "");
  }
  return path;
}

/**
 * Check if this is a Supabase product route (/product/:id)
 */
export function isSupabaseProductRoute(pathname) {
  return pathname.startsWith("/product/");
}

/**
 * Normalize Supabase product to the format expected by ProductDetailPage
 */
function mapSupabaseToProduct(dbProduct) {
  if (!dbProduct) return null;
  const img1 = resolveImageUrl(dbProduct.image_url);
  const img2 = dbProduct.image_url_2 ? resolveImageUrl(dbProduct.image_url_2) : img1;
  const img3 = dbProduct.image_url_3 ? resolveImageUrl(dbProduct.image_url_3) : (dbProduct.image_url_2 ? img2 : img1);
  const slug = dbProduct.slug || slugify(dbProduct.name);
  return {
    id: dbProduct.id,
    slug: slug || dbProduct.id,
    images: [img1, img2, img3],
    title: dbProduct.name,
    description: dbProduct.description || "",
    originalPrice: Math.round(dbProduct.price * 1.4) || dbProduct.price,
    offerPrice: Number(dbProduct.price),
    stockQuantity: dbProduct.stock_quantity,
    weight: Number(dbProduct.weight) || 0.3,
    category: dbProduct.category,
    isBestseller: Boolean(dbProduct.is_bestseller),
    rating: Number(dbProduct.rating) || 0,
    review_count: Number(dbProduct.review_count) || 0,
    similarProducts: [],
  };
}

/**
 * Fetch product from Supabase by UUID
 */
export async function getSupabaseProductById(id) {
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("id", id)
      .single();

    if (error || !data) return null;
    return mapSupabaseToProduct(data);
  } catch (err) {
    console.error("Error fetching product from Supabase:", err);
    return null;
  }
}

/**
 * Fetch product from Supabase by slug (URL-friendly name)
 * Falls back to matching by slugified name when slug column is empty or doesn't exist
 */
export async function getSupabaseProductBySlug(slug) {
  if (!slug) return null;
  try {
    // Try slug column first (if it exists and is populated)
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();

    if (!error && data) return mapSupabaseToProduct(data);

    // Fallback: fetch products and match by slugified name (handles missing slug column or null slugs)
    const { data: allData, error: allError } = await supabase
      .from("products")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);

    if (allError || !allData?.length) return null;

    const slugLower = slug.toLowerCase();
    const match = allData.find((p) => {
      const pSlug = (p.slug || slugify(p.name || "")).toLowerCase();
      return pSlug === slugLower;
    });
    return match ? mapSupabaseToProduct(match) : null;
  } catch (err) {
    console.error("Error fetching product by slug:", err);
    return null;
  }
}

/**
 * Fetch all products from Supabase
 */
export async function getAllSupabaseProducts() {
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) return [];
    return (data || []).map(mapSupabaseToProduct);
  } catch (err) {
    console.error("Error fetching products from Supabase:", err);
    return [];
  }
}

/**
 * Fetch products by max price (for Under ₹99, Under ₹499, etc.)
 * Price filters are NOT categories - products appear by price.
 */
export async function getSupabaseProductsByMaxPrice(maxPrice, limit = 50) {
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .lte("price", maxPrice)
      .order("price", { ascending: true })
      .limit(limit);

    if (error) return [];
    return (data || []).map(mapSupabaseToProduct);
  } catch (err) {
    console.error("Error fetching products by price:", err);
    return [];
  }
}

/**
 * Fetch products by max price AND category (for /under99/earrings etc.)
 */
export async function getSupabaseProductsByMaxPriceAndCategory(maxPrice, category, limit = 50) {
  if (!category) return getSupabaseProductsByMaxPrice(maxPrice, limit);
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .lte("price", maxPrice)
      .eq("category", category)
      .order("price", { ascending: true })
      .limit(limit);

    if (error) return [];
    return (data || []).map(mapSupabaseToProduct);
  } catch (err) {
    console.error("Error fetching products by price and category:", err);
    return [];
  }
}

/**
 * Fetch all sub-categories (Jewellery, Bags, Clothing) for price page filters
 */
export async function getAllSubCategories() {
  try {
    const { data, error } = await supabase
      .from("categories")
      .select("name")
      .or("description.eq.Jewellery Sub-category,description.eq.Bags Sub-category,description.eq.Clothing Sub-category")
      .order("name");

    if (error) return [];
    return (data || []).map((c) => c.name);
  } catch (err) {
    console.error("Error fetching sub-categories:", err);
    return [];
  }
}

/**
 * Fetch products by category (sub-category name)
 */
export async function getSupabaseProductsByCategory(category, limit = 100) {
  if (!category) return [];
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("category", category)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) return [];
    return (data || []).map(mapSupabaseToProduct);
  } catch (err) {
    console.error("Error fetching products by category:", err);
    return [];
  }
}

/**
 * Fetch products by category_id
 */
export async function getSupabaseProductsByCategoryId(categoryId, limit = 100) {
  if (!categoryId) return [];
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("category_id", categoryId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) return [];
    return (data || []).map(mapSupabaseToProduct);
  } catch (err) {
    console.error("Error fetching products by category_id:", err);
    return [];
  }
}

/**
 * Fetch products by multiple categories (e.g. Traditional Wear + Western Wear)
 */
export async function getSupabaseProductsByCategories(categories, limit = 100) {
  if (!categories?.length) return [];
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .in("category", categories)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) return [];
    return (data || []).map(mapSupabaseToProduct);
  } catch (err) {
    console.error("Error fetching products by categories:", err);
    return [];
  }
}

/**
 * Fetch Jewellery sub-categories from Dashboard (for Jewellery page filters)
 */
export async function getJewellerySubCategories() {
  try {
    const { data, error } = await supabase
      .from("categories")
      .select("name")
      .eq("description", "Jewellery Sub-category")
      .order("name");

    if (error) return [];
    return (data || []).map((c) => c.name);
  } catch (err) {
    console.error("Error fetching Jewellery sub-categories:", err);
    return [];
  }
}

/**
 * Fetch sub-sub categories for a given parent (sub-category name, e.g. "Necklaces")
 * Returns categories where parent_id points to the sub-category
 */
export async function getSubSubCategories(parentCategoryName) {
  if (!parentCategoryName) return [];
  try {
    const { data: parentRow, error: parentErr } = await supabase
      .from("categories")
      .select("id")
      .eq("name", parentCategoryName)
      .maybeSingle();

    if (parentErr || !parentRow?.id) return [];

    const { data, error } = await supabase
      .from("categories")
      .select("id, name, slug")
      .eq("parent_id", parentRow.id)
      .order("name");

    if (error) return [];
    return data || [];
  } catch (err) {
    console.error("Error fetching sub-sub categories:", err);
    return [];
  }
}

/**
 * Fetch all sub-sub categories for Jewellery (grouped by parent sub-category)
 * Returns { "Necklaces": [...], "Earrings": [...], ... }
 */
export async function getAllJewellerySubSubCategories() {
  try {
    const subCats = await getJewellerySubCategories();
    const result = {};
    for (const subName of subCats) {
      const children = await getSubSubCategories(subName);
      if (children.length > 0) result[subName] = children;
    }
    return result;
  } catch (err) {
    console.error("Error fetching Jewellery sub-sub categories:", err);
    return {};
  }
}

/**
 * Fetch main categories for Navbar (description = "Main Category", is_active = true)
 */
export async function getMainCategories() {
  try {
    const { data, error } = await supabase
      .from("categories")
      .select("name, slug")
      .eq("description", "Main Category")
      .order("name");

    if (error) return [];
    const list = data || [];
    if (list.length > 0) return list;
    return [];
  } catch (err) {
    console.error("Error fetching main categories:", err);
    return [];
  }
}

/**
 * Get similar products from Supabase (same category, excluding current)
 */
export async function getSimilarSupabaseProducts(categoryId, excludeId, limit = 6) {
  if (!categoryId) return [];
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("category", categoryId)
      .neq("id", excludeId)
      .limit(limit);

    if (error || !data?.length) return [];
    return data.map((p) => {
      const slug = p.slug || slugify(p.name);
      return {
        id: p.id,
        name: p.name,
        price: p.price,
        img: resolveImageUrl(p.image_url),
        link: `/product/${slug || p.id}`,
      };
    });
  } catch (err) {
    return [];
  }
}

/**
 * Sync lookup for static products only
 */
export function getStaticProductById(productId) {
  return getStaticProduct(productId);
}

/**
 * Unified product lookup: static first (sync), then Supabase (async)
 * - For static: productId is slug (e.g. "gold-bracelet-1")
 * - For Supabase: productId can be UUID or slug (e.g. /product/chintu)
 */
export async function getProductById(productId) {
  let staticProduct = getStaticProduct(productId);
  let supabaseProduct = null;

  // 1. Fetch Supabase product
  if (isUuid(productId)) {
    supabaseProduct = await getSupabaseProductById(productId);
  } else {
    supabaseProduct = await getSupabaseProductBySlug(productId);
    
    // If not found by slug/id, but static exists, try fetching DB product by exact title match (handling synced products)
    if (!supabaseProduct && staticProduct) {
      const { data } = await supabase
        .from("products")
        .select("*")
        .eq("name", staticProduct.title)
        .maybeSingle();

      if (data) supabaseProduct = mapSupabaseToProduct(data);
    }
  }

  // If supabaseProduct was found but staticProduct was null, try to find matching static product for images
  if (supabaseProduct && !staticProduct) {
    const staticMatchKey = Object.keys(PRODUCTS).find(k => PRODUCTS[k].title === supabaseProduct.title);
    if (staticMatchKey) staticProduct = PRODUCTS[staticMatchKey];
  }

  // 2. Merge resolving data priorities
  if (supabaseProduct && staticProduct) {
    const similar = await getSimilarSupabaseProducts(supabaseProduct.category, supabaseProduct.id, 6);
    return {
      ...staticProduct, // static base (arrays like images)
      ...supabaseProduct, // dynamic overrides (price, name, stock)
      
      // Preserve rich static images instead of the single DB URL, or merge them
      images: supabaseProduct.images && supabaseProduct.images[0] !== getPlaceholderImage()
        ? supabaseProduct.images
        : staticProduct.images,
        
      similarProducts: similar.length > 0 ? similar : getDefaultSimilarFromStatic(),
      source: "merged",
    };
  }

  // 3. Just Supabase
  if (supabaseProduct) {
    const similar = await getSimilarSupabaseProducts(supabaseProduct.category, supabaseProduct.id, 6);
    return {
      ...supabaseProduct,
      similarProducts: similar.length > 0 ? similar : getDefaultSimilarFromStatic(),
      source: "supabase",
    };
  }

  // 4. Just Static
  if (staticProduct) {
    return { ...staticProduct, source: "static" };
  }

  return null;
}

/**
 * Fallback similar products from static catalog when Supabase has no matches
 */
function getDefaultSimilarFromStatic() {
  const keys = Object.keys(PRODUCTS);
  return keys.slice(0, 6).map((k) => {
    const p = PRODUCTS[k];
    return {
      id: k,
      name: p.title,
      price: p.offerPrice,
      img: p.images[0],
      link: `/${k}`,
    };
  });
}

/**
 * Search all products (static + Supabase)
 */
export async function searchProducts(query) {
  if (!query || typeof query !== "string") return [];
  const qTemp = query.toLowerCase().trim();
  if(!qTemp) return [];

  const results = [];
  
  // Search static products
  if (PRODUCTS) {
    for (const [key, p] of Object.entries(PRODUCTS)) {
      if (p.title.toLowerCase().includes(qTemp) || (p.description && p.description.toLowerCase().includes(qTemp)) || key.toLowerCase().includes(qTemp)) {
        results.push({
          id: key,
          name: p.title,
          price: p.offerPrice,
          img: p.images?.[0] || getPlaceholderImage(),
          link: `/product/${slugify(key)}`
        });
      }
    }
  }

  // Search Supabase
  try {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .ilike("name", `%${qTemp}%`)
      .limit(10);
      
    if (!error && data) {
      for (const p of data) {
        // Check if there is already a static product with the exact same name
        const existingIndex = results.findIndex(r => r.name.toLowerCase() === p.name.toLowerCase());
        
        const slug = p.slug || slugify(p.name);
        const supabaseResult = {
          id: p.id,
          name: p.name,
          price: p.price,
          img: resolveImageUrl(p.image_url),
          link: `/product/${slug || p.id}`
        };

        if (existingIndex !== -1) {
          // Overwrite the static fallback with the real Supabase data (for updated prices)
          // But keep the static image if the DB simply has a placeholder
          if (supabaseResult.img === getPlaceholderImage() && results[existingIndex].img !== getPlaceholderImage()) {
            supabaseResult.img = results[existingIndex].img;
          }
          results[existingIndex] = supabaseResult;
        } else {
          results.push(supabaseResult);
        }
      }
    }
  } catch (err) {
    console.error("Error searching Supabase products", err);
  }

  return results.slice(0, 8); // return top 8 results
}

/**
 * Submit a review to Supabase
 * @param {string} productId - Product ID
 * @param {number} submittedRating - New rating
 * @param {string} reviewText - Optional review text
 * @param {number|null} oldRating - User's previous rating (if updating)
 */
export async function submitProductReview(productId, submittedRating, reviewText, oldRating = null) {
  try {
    // 1. Insert into reviews table (optional tracking)
    const { error: insertError } = await supabase
      .from("reviews")
      .insert([
        {
          product_id: String(productId),
          rating: Number(submittedRating),
          review_text: reviewText || "",
        },
      ]);
    
    // 2. Fetch current product rating & count to calculate rolling average
    const { data: currentProduct } = await supabase
      .from("products")
      .select("rating, review_count")
      .eq("id", productId)
      .maybeSingle();

    if (currentProduct) {
      let currentRating = Number(currentProduct.rating) || 0;
      let currentCount = Math.max(0, Number(currentProduct.review_count) || 0);
      
      let newCount, newRating;
      
      if (oldRating !== null && currentCount > 0) {
        // User is changing their existing rating
        newCount = currentCount;
        newRating = ((currentRating * currentCount) - Number(oldRating) + Number(submittedRating)) / newCount;
      } else {
        // User is rating for the first time
        newCount = currentCount + 1;
        newRating = ((currentRating * currentCount) + Number(submittedRating)) / newCount;
      }
      
      newRating = isFinite(newRating) ? Number(newRating.toFixed(2)) : 0;

      await supabase
        .from("products")
        .update({ rating: newRating, review_count: newCount })
        .eq("id", productId);
        
      return { data: { newRating, newCount }, error: null };
    }
    
    return { data: null, error: insertError };
  } catch (err) {
    console.error("Error submitting review:", err);
    return { error: err };
  }
}

/**
 * Get all reviews for a specific product
 */
export async function getReviewsForProduct(productId) {
  try {
    const { data, error } = await supabase
      .from("reviews")
      .select("*")
      .eq("product_id", String(productId))
      .order("created_at", { ascending: false });

    if (error) return [];
    return data || [];
  } catch (err) {
    console.error("Error fetching product reviews:", err);
    return [];
  }
}

/**
 * Get aggregated review data (avg rating, total count) for all products
 */
export async function getAllProductAggregatedReviews() {
  try {
    const { data, error } = await supabase.from("reviews").select("product_id, rating");
    if (error || !data) return {};

    const summary = {};
    data.forEach((r) => {
      const pid = r.product_id;
      if (!summary[pid]) {
        summary[pid] = { totalRating: 0, count: 0 };
      }
      summary[pid].totalRating += r.rating;
      summary[pid].count += 1;
    });

    const result = {};
    Object.keys(summary).forEach((pid) => {
      const s = summary[pid];
      result[pid] = {
        rating: s.totalRating / s.count,
        reviews: s.count,
      };
    });

    return result;
  } catch (err) {
    console.error("Error aggregating reviews:", err);
    return {};
  }
}
